import React, { useEffect, useState } from 'react'
import { styled } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { fetchProjects, getCachedProjects } from '../../../axios/projects';

const getProjectDisplayName = (project) => {
  const title = project?.title?.trim();

  if (title) {
    const titleWithoutType = title.split(/\s+for\s+/i)[1];
    return titleWithoutType?.trim() || title;
  }

  return project?.project_name?.trim() || 'Untitled project';
};

function ProjectsList({ category = 'residential' }) {

  const [projectArray, setprojectArray] = useState([]);
  const [requestStatus, setRequestStatus] = useState('loading');
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  const handleClick = (slug) => {
    navigate('/project/' + slug);
  };

  useEffect(() => {
    const controller = new AbortController();
    const pattern = [40, 24, 34, 28, 32, 38, 34, 40, 24];
    const mobilePattern = [60, 39, 39, 60];
    const addLayout = (projects) => projects.map((project, index) => ({
      ...project,
      width: pattern[index % pattern.length],
      smWidth: mobilePattern[index % mobilePattern.length],
    }));
    const cachedProjects = getCachedProjects(category);

    if (cachedProjects) {
      setprojectArray(addLayout(cachedProjects));
      setRequestStatus(cachedProjects.length ? 'success' : 'empty');
    } else {
      setprojectArray([]);
      setRequestStatus('loading');
    }

    fetchProjects(category, { signal: controller.signal })
      .then((projects) => {
        setprojectArray(addLayout(projects));
        setRequestStatus(projects.length ? 'success' : 'empty');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        // Keep showing the last successful response if a refresh fails.
        if (!cachedProjects) {
          setRequestStatus('error');
        }
        console.error('Unable to load projects:', error);
      });

    return () => controller.abort();
  }, [category, retryCount]);

  return (
    <Section>
      <ProjectListArea>
        {projectArray.map((obj) => (
          <SingleProject
            key={obj.id}
            w={obj.width}
            sm={obj.smWidth}
            onClick={() => handleClick(obj?.slug)}
          >
            <ImageContainer>
              <img
                loading="lazy"
                src={obj?.project_images?.[0]?.image}
                alt={getProjectDisplayName(obj)}
              />
            </ImageContainer>
            <Name>
              <p>{getProjectDisplayName(obj)}</p>
            </Name>
          </SingleProject>
        ))}
        {requestStatus === 'loading' && (
          <StatusMessage>Loading {category} projects…</StatusMessage>
        )}
        {requestStatus === 'error' && (
          <StatusMessage>
            Projects could not be loaded right now.
            <RetryButton type="button" onClick={() => setRetryCount((count) => count + 1)}>
              Try again
            </RetryButton>
          </StatusMessage>
        )}
        {requestStatus === 'empty' && (
          <EmptyState>
            No {category} projects have been added yet.
          </EmptyState>
        )}
      </ProjectListArea>
    </Section>
  );
}

export default ProjectsList;

const EmptyState = styled.p`
  width: 100%;
  margin: 40px 0 70px;
  text-align: center;
  font-family: 'raleway_regular';
`;

const StatusMessage = styled.div`
  width: 100%;
  margin: 40px 0 70px;
  text-align: center;
  font-family: 'raleway_regular';
`;

const RetryButton = styled.button`
  display: block;
  margin: 16px auto 0;
  padding: 10px 22px;
  border: 1px solid var(--primary-cl);
  background: var(--primary-cl);
  color: #fff;
  cursor: pointer;
  font-family: 'raleway_medium';
`;


const Section = styled.div`
  @media(max-width:580px){
    width:100vw;
    margin-left:-5.5%;
  }
`;

const ProjectListArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  /* grid-template-columns: 40% 60%; */
  row-gap: 15px;
  margin:20px 0;

  @media(max-width:580px){
  row-gap: 5px;
  }
`;

const LoadButton = styled.div`
width: 100%;
margin: 0px 0px 25px;
display: flex;
justify-content: center;
align-items:center;

@media(max-width:1400px){
    margin: 40px 0px 25px;
    
}

button{
    border:1px solid var(--primary-cl);
    background-color: white;
    padding: 12px  26px;
    font-size:14px;
    font-family: 'raleway_medium';
    color: #000;

    &:hover{
        background-color: var(--primary-cl);
        color: #fff;
    }
}
`;

const SingleProject = styled.div`
cursor: pointer;
${(props) => props.w && `width:${props.w}%;`}

@media(max-width: 1024px){
  ${(props) => props.sm && `width:${props.sm}%;`}
}

@media(max-width: 900px){
  width: 49%;
}
@media(max-width:580px){
    width:100%;
    position: relative;
    &::after{
      content:'';
      position: absolute;
      inset: 0;
      background-color:#000;
      opacity: .3;
    }
}

@media(max-width:480px){
  width: 100%;
}

`;

const ImageContainer = styled.div`
width: 100%;
position: relative;
height: 90%;
max-height: 464px;
@media(max-width:580px){
  height: 100%;

}
    &:after{
        content: '';
        width: 0%;
        height: 100%;
        background-color: #c6c1c1;
        top: 0;
        left: 0;
        position: absolute;
        opacity: 25%;
        transition: all .2s ease;
    }
    &:hover{
        &:after{
            width: 100%;
            transition: all 1.1s ease;
        }
    }
img{
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: center;
}
`;

const Name = styled.div`
@media(max-width:580px){
    position: absolute;
    top: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    z-index: 25;
}
p{
margin-top: 12px;
font-family: 'raleway_medium';
margin-bottom:16px;

@media(max-width:1400px){
    font-size:14px;
}

@media(max-width:580px){
    color: #fff;
    font-size:21px;
    text-align:center;
}

@media(max-width:480px){
    margin-top: 8px;
}
@media(max-width:360px){
    width:17ch;
    font-size:19px;
  }

}
`;
